import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { PgUserStatusService } from '../../pg-ui-molecules/pg-user-status.service';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';
import { LocalizationService } from '../../services/localization.service';
import { Websocket2Service, Websocket2Registration } from '../../services/websocket2.service';
import { WSServiceChatChannel, WSServiceChatMessage } from '../pg-chat.model';
import { CustomerCareBookingData, CustomerCareService } from '../../services/customer-care.service';
import { SoundService } from '../../services/sound.service';
import { EnvironmentService } from '../../services/environment.service';
import { PgChatChannelComponent } from '../pg-chat-channel/pg-chat-channel.component';

class WSServiceChatSession {
    id: number
    session_id:string
    username:string
    created_at:string
}

class WSServiceChatBooking {
    id:string;
    from:number;
    to:number;
    guest_name:string;
}

class WSServiceBookingStatus {
    id: string;
    booking: WSServiceChatBooking;
    started: boolean;
    customerCare: {
        user: string,
        online: boolean
    };
    guest: {
        user: string,
        online: boolean
    };
} 

@Component({
    selector: 'app-pg-chat',
    templateUrl: './pg-chat.component.html',
    styleUrls: ['./pg-chat.component.scss'],
    standalone: false
})
export class PgChatComponent implements OnInit, OnDestroy, AfterViewInit {

    constructor(protected websocketService:Websocket2Service, protected authService:AuthService, protected localizationService:LocalizationService, protected dataService:DataService, protected pgUserStatusService:PgUserStatusService, protected customerCareService:CustomerCareService, protected soundService:SoundService, protected environmentService:EnvironmentService) { }

    @Input() isHidden:boolean;
    @Input() isSilent:boolean;
    /** Peer chat vs customer-care agent console (apps host one mode each). */
    @Input() mode:'chat'|'customer-care' = 'chat';

    @Output() unreadCount = new EventEmitter<number>();
    @Output() notificationCount = new EventEmitter<number>();

    websocketRegistration:Websocket2Registration = null;
    protected _websocketRegistrationSubscriptions:Array<Subscription> = [];

    isLoading = false;

    unathorizedName = '';

    chatBooking:string = null;
    chatBookingData:WSServiceChatBooking = null;
    isChatBookingPast = false;

    realmId:string = null;
    customerCareChannel:string = null;

    customerCareBookingStatus:{ 
        [pid:string]: WSServiceBookingStatus 
    } = {}

    isLoggedIn = false;
    hasAccessError = false;

    isGuest = true;

    customerCareAvailability:number = null;

    channels:{ [id:string]: { 
        status: WSServiceChatChannel,
        unread: number,
        typing: { [user:string]: number }
    } } = {};

    currentChannel:{ [tab:string] : string } = {};

    private _componentSubscriptions:Array<Subscription> = [];
    private _resetTypingInterval = null;

    customerCareBookingsDay:{ [time:string]: CustomerCareBookingData } = null;

    private _checkNewBookingsInterval = null;
    private _warnCustomerCareInterval = null;

    customerCareBlink = false;

    notificationAudio:HTMLAudioElement = null;

    callAudioDevicesOptions:Array<{ value: string, text: string }> = [];
    callVideoDevicesOptions:Array<{ value: string, text: string }> = [];

    callAudioSource:string = null;
    callVideoSource:string = null;

    ngOnInit(): void {
        this.currentTab = this.mode == 'customer-care' ? 'customer_care' : 'chat';
        if(this.mode == 'customer-care') {
            this._serviceName = 'customer-care';
        }

        this.register();

        this._componentSubscriptions.push(this.pgUserStatusService.usersStatusIndexChange.subscribe(() => {
            this.generateUserList();
        }))

        this._componentSubscriptions.push(this.authService.statusChange.subscribe(() => {
            this.register();
        }))

        if(this.mode == 'customer-care') {
            let _wasCustomerCare = this.authService.profile?.customer_care;

            this._componentSubscriptions.push(this.authService.profileChange.subscribe(() => {
                let _isCustomerCare = this.authService.profile?.customer_care;

                if(_isCustomerCare != _wasCustomerCare) {
                    _wasCustomerCare = _isCustomerCare;
                    this.register()
                }
            }))

            let _wereChannels = JSON.stringify((this.authService.availability?.channels || []).sort());

            this._componentSubscriptions.push(this.authService.availabilityChange.subscribe(() => {
                let _areChannels = JSON.stringify((this.authService.availability?.channels || []).sort());

                if(_areChannels != _wereChannels) {
                    _wereChannels = _areChannels;
                    this.register()
                }
            }))
        }

        this._resetTypingInterval = setInterval(() => {
            let _now = Date.now();
            for(let i in this.channels) {
                let _toDelete = [];

                for(let j in this.channels[i].typing) {
                    if(this.channels[i].typing[j] + 2500 < _now) {
                        _toDelete.push(j)
                    }
                }

                for(let _id of _toDelete) {
                    delete this.channels[i].typing[_id]
                }
            }
        }, 1000)

        this.notificationAudio = this.soundService.add('notification')

        // TODO: queste parti della call andrebbero rifattorizzata nel componente della call

        navigator.mediaDevices.enumerateDevices().then((devices) => {
            for(let i = 0; i < devices.length; i ++){
                let _device = devices[i];
                
                if(_device.kind === 'audioinput') {
                    this.callAudioDevicesOptions.push({ value: _device.deviceId, text: _device.label || 'audio ' + (i + 1) })
                }
                else if(_device.kind === 'videoinput') {
                    this.callVideoDevicesOptions.push({ value: _device.deviceId, text: _device.label || 'video ' + (i + 1) })
                }
            };

            if(this.callAudioDevicesOptions.length == 0) {
                this.callAudioDisabled = true;
                this.callAudioSource = null;
            }
            else {
                this.callAudioSource = this.callAudioDevicesOptions[0].value;
            }

            if(this.callVideoDevicesOptions.length == 0) {
                this.callVideoDisabled = true;
                this.callVideoSource = null;
            }
            else {
                this.callVideoSource = this.callVideoDevicesOptions[0].value;
            }
        });
    }

    ngAfterViewInit() {
        this.notificationCount.emit(0);
        
        this._warnCustomerCareInterval = setInterval(() => {
            if(this.mode == 'customer-care' && this.customerCareWaitList != null && this.customerCareWaitList.length > 0) {
                if(!this.customerCareBlink) {
                    if(!this.isSilent) this.notificationAudio.play();
                    this.customerCareBlink = true;

                    setTimeout(() => {
                        this.customerCareBlink = false;
                    }, 8000)
                }

                this.notificationCount.emit(this.customerCareWaitList.length);
            }
            else {
                this.notificationCount.emit(0);
            }

            this._checkChatBookingPast();
        }, 1000)
    }

    ngOnDestroy() {
        this._cleanAccess();
        for(let _subscription of this._componentSubscriptions) {
            _subscription.unsubscribe();
        }

        clearInterval(this._resetTypingInterval)
        clearInterval(this._checkNewBookingsInterval)
        clearInterval(this._warnCustomerCareInterval)

        this.soundService.remove(this.notificationAudio)
    }

    private _checkUnreadCount() {
        if(!this.isLoggedIn) {
            this.unreadCount.emit(0)
        }
        else {
            this.unreadCount.emit(this.getUnreadSum())
        }
    }

    private _checkChatBookingPast() {
        this.isChatBookingPast = false;
        
        if(this.chatBookingData != null) this.isChatBookingPast = this.chatBookingData.from < Date.now()
    }

    register() {
        this._checkUnreadCount()

        if(this.authService.isLoggedIn() && !this._forceGuest) {
            this._handleAccess({ token: true });
        }   
        else if(this.authService.isLoggedIn() || this.canUnauthorizedLogIn() || this.chatBooking != null) {
            this._handleAccess({ name: this.unathorizedName, token: this.authService.isLoggedIn() });
        }
    }

    onUnathorizedNameKeyUp(event:KeyboardEvent) {
        if(event.key == 'Enter') {
            this.unauthorizedLogIn();
        }
    }

    canUnauthorizedLogIn() {
        return this.unathorizedName != null && this.unathorizedName != ''
    }

    unauthorizedLogIn() {
        if(this.canUnauthorizedLogIn()) {
            this._handleAccess({ name: this.unathorizedName })
        }
    }

    private _checkNewBookings() {
        let _now = new Date();

        this.customerCareService.getBookingsDay(this.authService.user.id.toString(), _now.getFullYear(), _now.getMonth() + 1, _now.getDate()).subscribe((data) => {
            if(data == null || Object.keys(data).length == 0) {
                this.customerCareBookingsDay = null;
            }
            else {
                this.customerCareBookingsDay = data;
            }
        })
    }

    protected _cleanAccess() {
        if(this.websocketRegistration != null) {
            this.websocketRegistration.unregister();
            this.websocketRegistration = null;
        }

        for(let _subscription of this._websocketRegistrationSubscriptions) _subscription.unsubscribe();
        this._websocketRegistrationSubscriptions = [];
    }

    protected _serviceName = 'chat'

    protected _forceGuest = false;

    protected sendChat(data:any) {
        if(this.websocketRegistration != null) {
            this.websocketRegistration.send(data);
        }
    }

    private _bindRegistrationMessages(registration:Websocket2Registration) {
        this._websocketRegistrationSubscriptions.push(registration.onMessage.subscribe((data) => {
            this._handleChatMessage(data);
        }))
    }

    private _handleChatMessage(data:any) {
        if(data == null) return;

        if(data.action == 'typing') {
            if(this.channels[data.target] != null) {
                if(data.content) this.channels[data.target].typing[data.from] = Date.now();
                else {
                    if(this.channels[data.target].typing[data.from] != null) delete this.channels[data.target].typing[data.from]
                }
            }
        }
        else if(data.action == 'message') {
            if(this.channels[data.target] != null && this.channels[data.target].status != null) {
                this.channels[data.target].status.messages.push(data);

                if(this.getCurrentChannel() != data.target) {
                    this.channels[data.target].unread++;
                }
                else if(this.chatChannel != null) {
                    this.chatChannel.requestScrollToBottom();
                }
            }
        }
        else if(data.action == 'join_channel' || data.action == 'open_direct') {
            this.channels[data.target] = {
                status: null,
                typing: {},
                unread: 0
            };

            this.generateChannelList()
            this.setCurrentChannel(data.target)
            this.generateUserList()
        }
        else if(data.action == 'leave_channel' || data.action == 'close_direct') {
            if(this.channels[data.target] != null) {
                if(this.getCurrentChannel() == data.target) {
                    this.unsetCurrentChannel()
                }

                delete this.channels[data.target];

                this.generateChannelList()
                this.generateUserList()
            }
        }
        else if(data.action == 'channel_status') {
            if(this.channels[data.target] != null) {
                this.channels[data.target].status = data.content;
                this.generateUserList()
            }
        }
        else if(data.action == 'channel_status.users') {
            if(this.channels[data.target] != null && this.channels[data.target].status != null) {
                this.channels[data.target].status.users = data.content;
                this.generateUserList()
            }
        }
        else if(/^channel_status\./.test(data.action)) {
            if(this.channels[data.target] != null && this.channels[data.target].status != null) {
                let _prop = data.action.split('.')[1];
                this.channels[data.target].status[_prop] = data.content;
            }
        }
        else if(data.action == 'direct_status') {
            if(this.channels[data.target] != null) {
                this.channels[data.target].status = {
                    users: null,
                    call: null,
                    id: data.target,
                    messages: data.content
                }
            }
        }
        else if(data.action == 'join_call') {
            this.callConfirmed = false;
            this.callChannel = data.target;
            this.callToken = data.content.token;
            this.callURL = data.content.url;
        }
        else if(data.action == 'leave_call') {
            if(this.callChannel == data.target) {
                this.callConfirmed = false;
                this.callChannel = null;
                this.callToken = null;
                this.callURL = null;
            }
        }
        else if(data.action == 'customercare_availability') {
            this.customerCareAvailability = data.content;
        }
        else if(data.action == 'booking') {
            this.chatBookingData = data.content;
            this._checkChatBookingPast();
        }
        else if(data.action == 'booking_status') {
            this.customerCareBookingStatus[data.content.id] = data.content;
        }

        this._checkUnreadCount();
    }

    private _createRegistration(service:string, authData:any) {
        if(authData.token) {
            return this.websocketService.register(service, async () => {
                let _data = JSON.parse(JSON.stringify(authData));
                _data.token = await this.authService.getAuthToken();
                return _data;
            })
        }
        else {
            let _data = JSON.parse(JSON.stringify(authData));
            _data.token = null;
            return this.websocketService.register(service, _data)
        }
    }

    protected _handleAccess(authData:{ token?:boolean, name?:string }) {
        this._cleanAccess();

        if(authData != null) {
            this.isLoggedIn = true;

            let _data = JSON.parse(JSON.stringify(authData))
            _data.booking = this.chatBooking;
            _data.realm = this.realmId;
            _data.channel = this.customerCareChannel;

            if(this._forceGuest) _data.guest = true;

            this.websocketRegistration = this._createRegistration(this._serviceName, _data);

            this.isLoading = true;
            this.hasAccessError = false;

            this._websocketRegistrationSubscriptions.push(this.websocketRegistration.statusChange.subscribe(() => {
                if(this.websocketRegistration.status == 'registered') {
                    this.isLoading = false;
                    this.hasAccessError = false;
                    this.isGuest = !!this.websocketRegistration.profile.guest;

                    if(this.isGuest || this.mode != 'customer-care') {
                        this.customerCareBookingsDay = null;
                        clearInterval(this._checkNewBookingsInterval)
                    }
                    else {
                        this._checkNewBookings()

                        this._checkNewBookingsInterval = setInterval(() => {
                            this._checkNewBookings()
                        }, 60000)
                    }
                }
                else {
                    this.customerCareBookingsDay = null;
                    clearInterval(this._checkNewBookingsInterval)

                    this.channels = {}
                    this.currentChannel = {};
                    this.callToken = null;
                    this.callChannel = null;
                    this.callURL = null;
                    this.isGuest = true;
                    this.customerCareAvailability = null;
                    this.chatBookingData = null;
                    this._checkChatBookingPast();

                    this.userList = [];
                    this.channelList = [];
                    this.customerCareWaitList = [];

                    if(this.websocketRegistration.status == 'unauthorized') {
                        this.isLoading = false;
                        this.hasAccessError = true;
                    }
                    else {
                        this.isLoading = true;
                        this.hasAccessError = false;
                    }
                }
            }))

            this._bindRegistrationMessages(this.websocketRegistration);
        }
    }

    getChannelTab(channel:string) {
        if(this.isGuestChannel(channel) || this.isCustomerCareChannel(channel)) return 'customer_care'
        else return 'chat'
    }

    unsetCurrentChannel() {
        let _currentChannel = this.getCurrentChannel() 
        let _setChannel = null;
    
        for(let i in this.channels) {
            let _beforeCurrent = true;

            if(this.getChannelTab(i) == this.currentTab) {
                if(i == _currentChannel) _beforeCurrent = false;
                else _setChannel = i;

                if(_setChannel != null && !_beforeCurrent) break;
            }
        }

        if(_setChannel == null) {
            for(let i in this.channels) {
                if(this.getChannelTab(i) == this.currentTab) {
                    _setChannel = i;
                    break;
                }
            }
        }

        this.setCurrentChannel(_setChannel)
    }

    setCurrentChannel(channel:string) {
        if(channel != null && this.getChannelTab(channel) != this.currentTab) {
            return;
        }

        this.currentChannel[this.currentTab] = channel;

        if(this.channels[channel] != null) {
            this.channels[channel].unread = 0;
        }

        if(this.chatChannel != null) {
            this.chatChannel.requestScrollToBottom(true);
        }

        this.channelSelectMenu?.close();
        this._checkUnreadCount();
    }

    getCurrentChannel() {
        return this.isHidden ? null : this.currentChannel[this.currentTab];
    }

    getCurrentChannelTyping() {
        let _channel = this.getCurrentChannel();
        if(this.channels[_channel] != null) {
            if(Object.keys(this.channels[_channel].typing).length > 0) {
                return this.channels[_channel].typing
            }
        }
        return null;
    }

    resolveUserName = (id:string) => this.getUserName(id);

    @ViewChild(PgChatChannelComponent) chatChannel:PgChatChannelComponent;

    onChannelSendMessage(text:string) {
        let _target = this.getCurrentChannel();
        if(_target == null || text == null || text == '') return;

        this.sendChat({
            action: 'message',
            target: _target,
            content: text
        })

        this.sendChat({
            action: 'typing',
            target: _target,
            content: false
        })
    }

    onChannelTypingChange(active:boolean) {
        let _target = this.getCurrentChannel();
        if(_target == null) return;

        this.sendChat({
            action: 'typing',
            target: _target,
            content: active
        })
    }

    onChannelFilesSelected(payload:{ files:Array<File>, text:string }) {
        this.setFiles(payload.files, payload.text);
    }

    isGuestUser(id:string) {
        return id != null && /^guest_/.test(String(id));
    }

    getUserName(id:string) {
        if(this.websocketRegistration != null && this.websocketRegistration.profile != null && String(this.websocketRegistration.profile.id) == String(id)) {
            return this.localizationService.translate('pg-customer-care.you')
        }

        if(this.isGuestUser(id)) {
            return id;
        }

        this.pgUserStatusService.watch(id);
        let _name = this.pgUserStatusService.getDisplayName(id);
        if(_name == null) return '...';
        return _name.replace(/\s+/g, ' ').replace(/^\s/g, '').replace(/\s$/g, '').replace(/\s([^\s])[^\s]+/g, ' $1.');
    }

    getUserStatusClass(id:string) {
        if(this.isGuestUser(id)) return 'success';
        return this.pgUserStatusService.getStatusClass(id);
    }

    getSessionStatusClass(session:any) {
        if(session.closed_at != null) return 'secondary';
        else return 'success'
    }

    leaveCurrentChannelOrDirect() {
        let _channel = this.getCurrentChannel();

        if(/^#/.test(_channel) || /^\?/.test(_channel)) {
            this.sendChat({
                action: 'leave_channel',
                target: _channel
            })
        }
        else if(/^@/.test(_channel)) {
            this.sendChat({
                action: 'close_direct',
                target: _channel
            })
        }
    }

    getMessageTime(message:WSServiceChatMessage) {
        return this.localizationService.format.time(message.time);
    }

    hasMessageDate(messages:Array<WSServiceChatMessage>, index) {
        if(index == 0) return true;
        else {
            return this.getMessageDate(messages[index]) != this.getMessageDate(messages[index - 1])
        }
    }

    hasMessageUserAndTime(messages:Array<WSServiceChatMessage>, index) {
        if(index == 0) return true;
        else if(messages[index].from != messages[index - 1].from) return true;
        else {
            return this.getMessageTime(messages[index]) != this.getMessageTime(messages[index - 1])
        }
    }

    getMessageDate(message:WSServiceChatMessage) {
        return this.localizationService.format.date(message.time);
    }

    getUnreadSum() {
        let _sum = 0;

        for(let i in this.channels) {
            _sum += this.channels[i].unread
        }

        return _sum;
    }

    getUserGuestChannel(user:string) {
        return user.replace(/^guest_/, '?');
    }

    getGuestChannelUser(channel:string) {
        return channel.replace(/^\?/, 'guest_');
    }
    
    openDirectOrGuestChannel(user:string) {
        if(/^guest_/.test(user)) {
            let _channel = this.getUserGuestChannel(user);

            if(this.channels[_channel] != null) {
                this.setCurrentChannel(_channel)
            }
            else {
                this.sendChat({
                    action: 'join_channel',
                    target: _channel
                })
            }
        }
        else {
            let _channel = '@' + user;

            if(this.channels[_channel] != null) {
                this.setCurrentChannel(_channel)
            }
            else {
                this.sendChat({
                    action: 'open_direct',
                    target: _channel
                })
            }
        }
    }

    getChannelName(id:string) {
        if(/^@/.test(id)) {
            return this.getUserName(id.replace(/^@/, ''))
        }
        else if(/^\?/.test(id)) {
            return this.getUserName(id.replace(/^\?/, 'guest_'))
        }
        else if(this.isRealmChannel(id)) {
            let _realmId = id.replace(/^#realm-/, '');
            let _realmName = _realmId == 'null'
                ? this.environmentService.getRealmName(null)
                : this.environmentService.getRealmName(_realmId);
            return '#' + (_realmName || _realmId);
        }
        else if(this.isCustomerCareChannel(id)) {
            if(/^#customercare/.test(id)) {
                let _channelPart = id.split('.')[1];
                if(_channelPart == null) _channelPart = '';
                else _channelPart = ' - ' + _channelPart

                let _cleanId = id.replace(/\..*$/, '');

                let _realmId = _cleanId.split('-')[1]

                if(_realmId == 'global') {
                    return '#Global' + _channelPart;
                }
                else {
                    return '#' + this.environmentService.getRealmName(_realmId) + _channelPart;
                }
            }
            else {
                return id;
            }
        }
        else return id;
    }

    getChannelStatusClass(id:string) {
        if(/^@/.test(id)) {
            return this.getUserStatusClass(id.replace(/^@/, ''))
        }
        else if(/^\?/.test(id)) {
            return this.getUserStatusClass(id.replace(/^\?/, 'guest_'))
        }
        else return null;
    }

    callToken:string = null;
    callChannel:string = null;
    callURL:string = null;

    callCompressed = false;
    callConfirmed = false;

    callAudioDisabled = false;
    callVideoDisabled = false;

    joinCallCurrentChannel() {
        let _channel = this.getCurrentChannel();

        if(this.callChannel != _channel) {
            this.leaveCurrentCall();
            
            this.sendChat({
                action: 'join_call',
                target: _channel
            })
        }
    }

    getCallParticipantsCount(channel:WSServiceChatChannel) {
        if(channel != null && channel.call != null) {
            return Object.keys(channel.call).length;
        }
    }

    getCallParticipantsList(channel:WSServiceChatChannel) {
        let _retVal = '';

        if(channel != null && channel.call != null) {
            for(let i in channel.call) {
                if(_retVal != '') _retVal += ', '
                return this.getUserName(i)
            }
        }

        return _retVal;
    }

    leaveCurrentCall() {
        if(this.callChannel != null) {
            this.sendChat({
                action: 'leave_call',
                target: this.callChannel
            })
        }
    }

    isDirectChannel(channel:string) {
        return /^@/.test(channel)
    }

    isGuestChannel(channel:string) {
        return /^\?/.test(channel);
    }

    isLoadingGuestSessions = false;

    onGuestSessionsOpenChange(open:boolean) {
        if(open) {
            this.loadGuestSessions();
        }
    }

    guestSessions:Array<any> = []

    loadGuestSessions() {
        this.isLoadingGuestSessions = true;
        this.guestSessions = [];

        this.dataService.getResourceData('ChatSession', { filter: [
            { field: 'closed_at', operator: '==', value: null }
        ] }).subscribe((data) => {
            this.isLoadingGuestSessions = false;
            this.guestSessions = data;

            this.dataService.getResourceData('ChatSession', { filter: [
                { field: 'closed_at', operator: '!=', value: null },
                { field: 'message_count', operator: '>', value: [ 0 ] },
            ] }).subscribe((data) => {
                for(let _item of data) {
                    let _isAlreadyIn = false;

                    for(let _session of this.guestSessions) {
                        if(_session.id == _item.id) {
                            _isAlreadyIn = true;
                            break;
                        }
                    }

                    if(!_isAlreadyIn) {
                        this.guestSessions.push(_item)
                    }
                }
            }, () => {
                this.isLoadingGuestSessions = false;
            })
        }, () => {
            this.isLoadingGuestSessions = false;
        })
    }

    getSessionDateTime(session:WSServiceChatSession) {
        return this.localizationService.format.datetime(session.created_at)
    }

    joinGuestSession(session:WSServiceChatSession) {
        let _channel = '?' + session.session_id;

        if(this.channels[_channel] != null) {
            this.setCurrentChannel(_channel)
        }
        else {
            this.sendChat({
                action: 'join_channel',
                target: _channel
            })
        }
    }
    
    isCustomerCareEnabled() {
        for(let i in this.channels) {
            if(this.channels[i] != null && this.isCustomerCareChannel(i)) return true;
        }

        return false;
    }

    isCustomerCareChannel(channel:string) {
        return /^#customercare/.test(channel)
    }

    isRealmChannel(channel:string) {
        return /^#realm-/.test(channel)
    }

    userList:Array<string> = [];
    customerCareWaitList:Array<string> = [];

    generateUserList() {
        this.customerCareWaitList = [];
        this.userList = [];

        let _selfId = this.websocketRegistration != null && this.websocketRegistration.profile != null
            ? String(this.websocketRegistration.profile.id)
            : null;
        
        for(let i in this.channels) {
            if(this.channels[i].status != null && this.channels[i].status.users != null) {
                for(let _user of this.channels[i].status.users) {
                    if(this.isGuestUser(_user)) {
                        if(String(_user) != _selfId && this.customerCareWaitList.indexOf(_user) == -1) {
                            this.customerCareWaitList.push(_user);
                        }
                    }
                    else {
                        let _status = this.pgUserStatusService.getStatus(_user);
                        if(_status != 'offline' && (this.authService.user == null || String(_user) != String(this.authService.user.id))) {
                            if(this.userList.indexOf(_user) == -1) this.userList.push(_user)
                            this.pgUserStatusService.watch(_user);
                        }
                    }
                }
            }
        }

        this.userList.sort((a, b) => {
            let _aVal = this.getUserName(a);
            let _bVal = this.getUserName(b);

            if(_aVal < _bVal) return -1;
            else if(_aVal > _bVal) return 1;
            else return 0;
        })
    }

    channelList:Array<string> = [];

    private _getChannelTypeOrder(channel:string) {
        if(this.isRealmChannel(channel)) return 0;
        else if(/^#/.test(channel)) return 10;
        else if(this.isDirectChannel(channel)) return 20;
        else if(this.isCustomerCareChannel(channel)) return 30;
        else if(this.isGuestChannel(channel)) return 40;
        else return 1000
    }

    generateChannelList() {
        this.channelList = [];

        for(let i in this.channels) {
            this.channelList.push(i)
        }

        this.channelList.sort((a, b) => {
            let _aType = this._getChannelTypeOrder(a)
            let _bType = this._getChannelTypeOrder(b)

            if(_aType != _bType) {
                if(_aType < _bType) return -1;
                else if(_aType > _bType) return 1;
                else return 0
            }
            else {
                let _aName = this.getChannelName(a)
                let _bName = this.getChannelName(b)
    
                if(_aName < _bName) return -1;
                else if(_aName > _bName) return 1;
                else return 0
            }
        })
    }

    currentTab:string = 'chat';

    setCurrentTab(tab:string) {
        this.currentTab = tab;
        if(this.chatChannel != null) {
            this.chatChannel.requestScrollToBottom(true);
        }
    }

    @ViewChild('channelSelectMenu') channelSelectMenu:NgbDropdown

    joinChannelId:string = '';

    onJoinChannelIdKeyUp(event:KeyboardEvent) {
        if(event.key == 'Enter') {
            this.joinChannel()
        }
    }

    joinChannel() {
        if(this.joinChannelId != '' && this.joinChannelId != '#') {
            let _channel = '#' + this.joinChannelId.replace(/^#*/, '');

            if(this.channels[_channel] != null) {
                this.setCurrentChannel(_channel)
            }
            else {
                this.sendChat({
                    action: 'join_channel',
                    target: _channel
                })    
            }
    
            this.joinChannelId = '';

            this.channelSelectMenu?.close();
        }
    }

    onChatWithUserClick(user:string) {
        this.openDirectOrGuestChannel(user);

        this.channelSelectMenu?.close();
    }

    isSaving = false;

    setFiles(files:Array<File>, messageText?:string) {
        if(!this.isSaving && files != null && files.length > 0) {
            this.isSaving = true;

            let _channel = this.getCurrentChannel();
            let _message = messageText || '';
            
            this.dataService.postFile('File', files[0]).subscribe((data) => {
                if(data.complete) {
                    this.isSaving = false;

                    if(data.data != null) {
                        this.sendChat({
                            action: 'message',
                            target: _channel,
                            content: _message,
                            data: {
                                type: 'attachment',
                                name: data.data.nome_file,
                                url: data.data.url
                            }
                        })
                    }
                }
            })
        }
    }
}
